import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronDown, ChevronRight, Activity } from 'lucide-react';
import { apiService } from '../../services/apiService';

interface LogEntry {
  id: string;
  ts: string;
  type: 'inbound' | 'outbound';
  method?: string;
  path?: string;
  status?: number;
  duration_ms?: number;
  req_body?: unknown;
  res_body?: unknown;
  service?: string;
  error_msg?: string;
  detail?: string;
}

interface StatsBuckets {
  '2xx': number;
  '3xx': number;
  '4xx': number;
  '5xx': number;
  outbound_errors: number;
}

interface Stats {
  buckets: StatsBuckets;
  top_error_paths: { path: string; count: number }[];
  total: number;
  error_rate: string;
}

type StatusFilter = 'errors' | '4xx' | '5xx' | 'outbound' | 'all';

const PIE_COLORS: { key: keyof StatsBuckets; label: string; color: string }[] = [
  { key: '2xx', label: '2xx OK', color: '#22c55e' },
  { key: '3xx', label: '3xx Redirect', color: '#60a5fa' },
  { key: '4xx', label: '4xx Client', color: '#f59e0b' },
  { key: '5xx', label: '5xx Server', color: '#ef4444' },
  { key: 'outbound_errors', label: 'Outbound', color: '#a855f7' },
];

function PieChart({ buckets }: { buckets: StatsBuckets }) {
  const data = PIE_COLORS.map(c => ({ ...c, value: buckets[c.key] || 0 })).filter(d => d.value > 0);
  const total = data.reduce((s, d) => s + d.value, 0);

  if (total === 0) {
    return (
      <div className="w-28 h-28 rounded-full bg-brand-surface-2 border-2 border-dashed border-brand-border flex items-center justify-center">
        <span className="text-[10px] text-brand-text-muted text-center leading-tight px-2">Keine Daten</span>
      </div>
    );
  }

  let pct = 0;
  const parts = data.map(d => {
    const start = pct;
    pct += (d.value / total) * 100;
    return `${d.color} ${start.toFixed(2)}% ${pct.toFixed(2)}%`;
  });

  return (
    <div
      className="w-28 h-28 rounded-full shrink-0"
      style={{ background: `conic-gradient(${parts.join(', ')})` }}
      title={data.map(d => `${d.label}: ${d.value} (${((d.value / total) * 100).toFixed(1)}%)`).join('\n')}
    />
  );
}

function PieLegend({ buckets }: { buckets: StatsBuckets }) {
  const total = PIE_COLORS.reduce((s, c) => s + (buckets[c.key] || 0), 0);
  return (
    <div className="flex flex-col gap-1.5">
      {PIE_COLORS.map(c => {
        const v = buckets[c.key] || 0;
        if (v === 0 && total > 0) return null;
        const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
        return (
          <div key={c.key} className="flex items-center gap-2 text-[12px]">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: c.color }} />
            <span className="text-brand-text-muted">{c.label}</span>
            <span className="font-semibold text-brand-text ml-auto pl-3">{v}</span>
            <span className="text-brand-text-muted w-10 text-right">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }: { status?: number }) {
  if (!status) return null;
  let cls = 'bg-green-100 text-green-700';
  if (status >= 500) cls = 'bg-red-100 text-red-700';
  else if (status >= 400) cls = 'bg-amber-100 text-amber-700';
  else if (status >= 300) cls = 'bg-blue-100 text-blue-700';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold ${cls}`}>
      {status}
    </span>
  );
}

function MethodBadge({ method }: { method?: string }) {
  if (!method) return null;
  const MAP: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700',
    POST: 'bg-green-100 text-green-700',
    PUT: 'bg-yellow-100 text-yellow-700',
    PATCH: 'bg-orange-100 text-orange-700',
    DELETE: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold ${MAP[method] ?? 'bg-gray-100 text-gray-700'}`}>
      {method}
    </span>
  );
}

function JsonBlock({ data, label }: { data: unknown; label: string }) {
  if (data === undefined || data === null) return null;
  return (
    <div className="mt-2">
      <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold mb-1">{label}</p>
      <pre className="text-[11px] bg-brand-surface-2 border border-brand-border rounded p-2 overflow-x-auto max-h-48 text-brand-text leading-relaxed">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function formatTime(ts: string) {
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(ts: string) {
  return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

const FILTER_CHIPS: { key: StatusFilter; label: string }[] = [
  { key: 'errors', label: 'Fehler' },
  { key: '4xx', label: '4xx' },
  { key: '5xx', label: '5xx' },
  { key: 'outbound', label: 'Outbound' },
  { key: 'all', label: 'Alle' },
];

const METHODS = ['all', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export const EventLogPage: React.FC = () => {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('errors');
  const [methodFilter, setMethodFilter] = useState('all');
  const [pathSearch, setPathSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [logsRes, statsRes] = await Promise.all([
        apiService.getEventLogs(),
        apiService.getEventLogStats(),
      ]);
      setEntries((logsRes.data as any).data ?? []);
      setStats((statsRes.data as any).data ?? null);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = entries.filter(e => {
    if (statusFilter === 'errors') {
      const isError = (e.type === 'inbound' && e.status != null && e.status >= 400) || e.type === 'outbound';
      if (!isError) return false;
    } else if (statusFilter === '4xx') {
      if (!(e.status != null && e.status >= 400 && e.status < 500)) return false;
    } else if (statusFilter === '5xx') {
      if (!(e.status != null && e.status >= 500)) return false;
    } else if (statusFilter === 'outbound') {
      if (e.type !== 'outbound') return false;
    }

    if (methodFilter !== 'all' && e.method !== methodFilter) return false;
    if (pathSearch && e.path && !e.path.toLowerCase().includes(pathSearch.toLowerCase())) return false;
    if (pathSearch && !e.path && e.service && !e.service.toLowerCase().includes(pathSearch.toLowerCase())) return false;

    return true;
  });

  const errorCount = stats
    ? (stats.buckets['4xx'] || 0) + (stats.buckets['5xx'] || 0) + (stats.buckets.outbound_errors || 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Activity className="h-5 w-5 text-brand-primary" strokeWidth={1.75} />
          <h1 className="text-lg font-semibold text-brand-text">Event Log</h1>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-accent/10 text-brand-primary uppercase tracking-wider">
            Letzte 24h
          </span>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-[11px] text-brand-text-muted">
              Zuletzt: {lastRefresh.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-surface-2 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
            Neu laden
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* Stats row */}
      {stats && (
        <div className="bg-brand-surface border border-brand-border rounded-lg p-4">
          <div className="flex items-start gap-6 flex-wrap">
            {/* Pie chart + legend */}
            <div className="flex items-center gap-4 shrink-0">
              <PieChart buckets={stats.buckets} />
              <PieLegend buckets={stats.buckets} />
            </div>

            {/* Stat cards */}
            <div className="flex gap-3 flex-wrap flex-1 min-w-0">
              <div className="bg-brand-surface-2 rounded-md px-4 py-3 min-w-[100px]">
                <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold">Gesamt</p>
                <p className="text-2xl font-bold text-brand-text mt-0.5">{stats.total.toLocaleString()}</p>
              </div>
              <div className="bg-brand-surface-2 rounded-md px-4 py-3 min-w-[100px]">
                <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold">Fehler</p>
                <p className={`text-2xl font-bold mt-0.5 ${errorCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {errorCount.toLocaleString()}
                </p>
              </div>
              <div className="bg-brand-surface-2 rounded-md px-4 py-3 min-w-[100px]">
                <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold">Fehlerrate</p>
                <p className={`text-2xl font-bold mt-0.5 ${parseFloat(stats.error_rate) > 5 ? 'text-red-600' : 'text-brand-text'}`}>
                  {stats.error_rate}
                </p>
              </div>
              {stats.top_error_paths.length > 0 && (
                <div className="bg-brand-surface-2 rounded-md px-4 py-3 flex-1 min-w-[160px]">
                  <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold mb-1.5">Top-Fehler-Endpoints</p>
                  <div className="space-y-1">
                    {stats.top_error_paths.slice(0, 3).map(p => (
                      <div key={p.path} className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono text-brand-text truncate flex-1">{p.path}</span>
                        <span className="font-semibold text-red-600 shrink-0">×{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-brand-surface border border-brand-border rounded-md p-0.5">
          {FILTER_CHIPS.map(chip => (
            <button
              key={chip.key}
              onClick={() => setStatusFilter(chip.key)}
              className={`px-3 py-1 text-[12px] font-medium rounded transition-colors cursor-pointer ${
                statusFilter === chip.key
                  ? 'bg-brand-primary text-white shadow-sm'
                  : 'text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <select
          value={methodFilter}
          onChange={e => setMethodFilter(e.target.value)}
          className="text-[12px] border border-brand-border rounded-md px-2 py-1.5 bg-brand-surface text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
        >
          {METHODS.map(m => (
            <option key={m} value={m}>{m === 'all' ? 'Alle Methoden' : m}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Pfad suchen…"
          value={pathSearch}
          onChange={e => setPathSearch(e.target.value)}
          className="text-[12px] border border-brand-border rounded-md px-3 py-1.5 bg-brand-surface text-brand-text placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 w-48"
        />

        <span className="text-[11px] text-brand-text-muted ml-auto">
          {filtered.length} Einträge
        </span>
      </div>

      {/* Table */}
      <div className="bg-brand-surface border border-brand-border rounded-lg overflow-hidden">
        {loading && entries.length === 0 ? (
          <div className="divide-y divide-brand-border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3 animate-pulse">
                <div className="h-3 w-12 bg-brand-surface-2 rounded" />
                <div className="h-3 w-10 bg-brand-surface-2 rounded" />
                <div className="h-3 w-8 bg-brand-surface-2 rounded" />
                <div className="h-3 flex-1 bg-brand-surface-2 rounded" />
                <div className="h-3 w-10 bg-brand-surface-2 rounded" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-2 text-brand-text-muted">
            <Activity className="h-8 w-8 opacity-30" strokeWidth={1.5} />
            <p className="text-[13px]">
              {statusFilter === 'errors' || statusFilter === '4xx' || statusFilter === '5xx'
                ? 'Keine Fehler in den letzten 24h'
                : 'Keine Einträge gefunden'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-brand-border">
            {/* Column headers */}
            <div className="hidden sm:grid grid-cols-[120px_60px_60px_1fr_70px_24px] gap-3 px-4 py-2 text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold bg-brand-surface-2">
              <span>Zeit</span>
              <span>Method</span>
              <span>Status</span>
              <span>Pfad / Service</span>
              <span className="text-right">Dauer</span>
              <span />
            </div>

            {filtered.map(entry => {
              const expanded = expandedIds.has(entry.id);
              const hasDetail = entry.req_body !== undefined || entry.res_body !== undefined || entry.error_msg;
              return (
                <div key={entry.id} className="divide-y divide-brand-border">
                  <div
                    className={`grid grid-cols-[120px_60px_60px_1fr_70px_24px] gap-3 px-4 py-3 items-center text-[12px] transition-colors ${
                      hasDetail ? 'cursor-pointer hover:bg-brand-surface-2' : ''
                    } ${entry.type === 'outbound' ? 'bg-purple-50/30' : ''}`}
                    onClick={() => hasDetail && toggleExpand(entry.id)}
                  >
                    <div className="flex flex-col leading-tight">
                      <span className="font-mono text-brand-text">{formatTime(entry.ts)}</span>
                      <span className="text-brand-text-muted text-[10px]">{formatDate(entry.ts)}</span>
                    </div>

                    <div>
                      {entry.type === 'outbound' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">OUT</span>
                      ) : (
                        <MethodBadge method={entry.method} />
                      )}
                    </div>

                    <div>
                      {entry.type === 'outbound' ? (
                        <span className="text-[11px] text-purple-600 font-medium">{entry.service}</span>
                      ) : (
                        <StatusBadge status={entry.status} />
                      )}
                    </div>

                    <div className="truncate font-mono text-[11px] text-brand-text">
                      {entry.type === 'outbound' ? (
                        <span className="text-brand-text-muted truncate">{entry.error_msg}</span>
                      ) : (
                        entry.path
                      )}
                    </div>

                    <div className="text-right text-brand-text-muted font-mono">
                      {entry.duration_ms != null ? `${entry.duration_ms}ms` : '—'}
                    </div>

                    <div className="flex items-center justify-end">
                      {hasDetail && (
                        expanded
                          ? <ChevronDown className="h-3.5 w-3.5 text-brand-text-muted" strokeWidth={2} />
                          : <ChevronRight className="h-3.5 w-3.5 text-brand-text-muted" strokeWidth={2} />
                      )}
                    </div>
                  </div>

                  {expanded && (
                    <div className="px-4 py-3 bg-brand-surface-2 space-y-1">
                      {entry.error_msg && entry.type === 'outbound' && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold mb-1">Fehler</p>
                          <p className="text-[12px] text-red-600 font-mono">{entry.error_msg}</p>
                          {entry.detail && <p className="text-[11px] text-brand-text-muted mt-0.5">{entry.detail}</p>}
                        </div>
                      )}
                      <JsonBlock data={entry.req_body} label="Request Body" />
                      <JsonBlock data={entry.res_body} label="Response Body" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventLogPage;
